// frontend/src/services/apService.ts
/**
 * AP Credits Service
 * Handles all AP-related API calls including score management and evaluation.
 *
 * Fully migrated to use typedApi for type safety.
 */

import { typedApi } from './typedApiClient';

class APService {
  /**
   * Get all available AP exams
   * @returns List of AP exams with id, name, requires_advisor
   */
  async getAPExams() {
    const data = await typedApi.apExams.list();
    return data.exams || [];
  }

  /**
   * Get user's AP scores
   * @returns User's AP scores with exam details
   */
  async getUserAPScores() {
    const data = await typedApi.apExams.listScores();
    return data.scores || [];
  }

  /**
   * Add or update an AP score
   * @param apExamId - AP exam ID
   * @param score - Score (3-5)
   * @param yearTaken - Year taken (optional)
   * @param courseChoice - Course choice if exam requires selection (optional)
   * @returns Updated score data
   */
  async addAPScore(
    apExamId: number,
    score: number,
    yearTaken: number | null = null,
    courseChoice: string | null = null
  ) {
    if (!apExamId || !score) {
      throw new Error('Exam ID and score are required');
    }

    if (score < 3 || score > 5) {
      throw new Error('AP score must be between 3 and 5');
    }

    return typedApi.apExams.addScore({
      ap_exam_id: apExamId,
      score: score,
      year_taken: yearTaken || undefined,
      course_choice: courseChoice || undefined,
    });
  }

  /**
   * Check if an AP exam score requires a course or gen-ed choice
   * @param examName - Name of the AP exam
   * @param score - Score (3-5)
   * @returns Choice information if required, null otherwise
   */
  async checkAPChoice(examName: string, score: number) {
    if (!examName || !score) {
      throw new Error('Exam name and score are required');
    }

    return typedApi.apExams.checkChoice({
      exam_name: examName,
      score: score,
    });
  }

  /**
   * Remove an AP score
   * @param scoreId - AP score ID to remove
   * @returns Result of deletion
   */
  async removeAPScore(scoreId: number) {
    if (!scoreId) {
      throw new Error('Score ID is required');
    }

    return typedApi.apExams.deleteScore(scoreId);
  }

  /**
   * Manually trigger AP credit evaluation
   * @returns Evaluation result with courses and credits
   */
  async evaluateAPCredits() {
    const data = await typedApi.apExams.evaluate();
    return data.evaluation;
  }

  /**
   * Get AP evaluation status/summary
   * @returns Current AP status with totals
   */
  async getAPStatus() {
    const data = await typedApi.apExams.getStatus();
    return data.status;
  }

}

// Export a singleton instance
export const apService = new APService();

// Also export the class for testing
export default APService;
